import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { AssetDTOView } from '../lib/types.js';
import { StatusBadge } from '../components/StatusBadge.js';

export function AssetsPage() {
  const query = useQuery({
    queryKey: ['assets'],
    queryFn: () => api.get<AssetDTOView[]>('/assets?pageSize=100'),
  });

  return (
    <section>
      <h1>Assets</h1>
      {query.data && query.data.data.length === 0 && (
        <p className="muted">No generated assets yet.</p>
      )}
      <div className="card-grid">
        {query.data?.data.map((a) => (
          <Link key={a.id} to={`/assets/${a.id}`} className="asset-card">
            <img src={`/api/v1/assets/${a.id}/content`} alt="asset" />
            <StatusBadge status={a.reviewStatus} />
          </Link>
        ))}
      </div>
    </section>
  );
}

export function AssetDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['assets', id],
    queryFn: () => api.get<AssetDTOView>(`/assets/${id}`),
  });
  const review = useMutation({
    mutationFn: (decision: 'reviewed' | 'rejected') =>
      api.post<AssetDTOView>(`/assets/${id}/review`, { decision }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assets', id] });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });

  if (query.isLoading) return <p>Loading…</p>;
  if (!query.data) {
    return (
      <p role="alert" className="form-error">
        Asset not found.
      </p>
    );
  }
  const a = query.data.data;

  return (
    <section>
      <p>
        <Link to="/assets">← Back to assets</Link>
      </p>
      <div className="page-head">
        <h1>Asset candidate</h1>
        <StatusBadge status={a.reviewStatus} />
      </div>
      <img className="asset-preview" src={`/api/v1/assets/${a.id}/content`} alt="asset" />
      <dl className="detail-grid">
        <dt>Dimensions</dt>
        <dd>
          {a.width}×{a.height}
        </dd>
        <dt>Type</dt>
        <dd>{a.mimeType}</dd>
        <dt>Provider</dt>
        <dd>
          {a.providerResponse.provider} / {a.providerResponse.model} ({a.providerResponse.quality})
        </dd>
        <dt>SHA-256</dt>
        <dd>
          <code>{a.sha256.slice(0, 24)}…</code>
        </dd>
      </dl>
      {a.reviewStatus === 'pending' && (
        <div className="button-row">
          <button
            type="button"
            onClick={() => review.mutate('reviewed')}
            disabled={review.isPending}
          >
            Mark reviewed
          </button>
          <button
            type="button"
            className="button--danger"
            onClick={() => review.mutate('rejected')}
            disabled={review.isPending}
          >
            Reject
          </button>
        </div>
      )}
    </section>
  );
}
